import { type HTMLAttributes, type Ref, useEffect, useMemo, useRef } from "react";
import {
	DocumentViewer,
	type DocumentViewerHandle,
	type PDFViewerControl,
	type ViewerSession,
} from "../document-viewer";

export type { PDFViewerControl };

export type PDFViewerOptions = {
	/** Opens at fit-to-width rather than automatic zoom, which reads as zoomed out in narrow columns. */
	fitToWidth?: boolean;
	/** One-based page the viewer opens on. Changing it moves the open viewer to that page. */
	page?: number;
	/**
	 * Bump it to send the open viewer back to `page` when `page` hasn't changed, e.g. a second
	 * click on the same citation after the reader scrolled away.
	 */
	pageRequestId?: number;
};

const STANDARD_EMBED_CLASS_NAME = "h-[80vh] w-[95vw] max-w-[775px]";

export type PDFViewerProps = Omit<HTMLAttributes<HTMLDivElement>, "children"> & {
	ref?: Ref<HTMLDivElement>;
	control: PDFViewerControl;
	options?: PDFViewerOptions;
	/** Shown in place of the generic error when the fetch 404s; only the caller knows what that means. */
	notFoundMessage?: string;
	/** Names the surface, e.g. `wip-statements-attachment-viewer`; every control's id starts with it. */
	trackingId: string;
	testId?: string;
};

/**
 * Fetches and shows a PDF in the `DocumentViewer`. Kept for callers that only need a document
 * on screen; consumers supply size via `className` and decide where it lives (modal, floating
 * panel, inline).
 */
export function PDFViewer({
	control,
	options,
	notFoundMessage,
	className = STANDARD_EMBED_CLASS_NAME,
	trackingId,
	testId = "pdf-viewer",
	ref,
	...rest
}: PDFViewerProps) {
	const page = options?.page;
	const pageRequestId = options?.pageRequestId;
	const fitToWidth = options?.fitToWidth ?? false;
	const initialSession = useMemo<Partial<ViewerSession>>(
		() => ({ zoom: fitToWidth ? "page-width" : "auto", ...(page === undefined ? {} : { page }) }),
		[fitToWidth, page],
	);
	const viewer = useRef<DocumentViewerHandle>(null);
	const shownRequest = useRef({ page, pageRequestId });
	useEffect(() => {
		const shown = shownRequest.current;
		if (page === undefined || (page === shown.page && pageRequestId === shown.pageRequestId))
			return;
		shownRequest.current = { page, pageRequestId };
		viewer.current?.goToPage(page);
	}, [page, pageRequestId]);
	return (
		<DocumentViewer
			ref={viewer}
			rootRef={ref}
			control={control}
			initialSession={initialSession}
			notFoundMessage={notFoundMessage}
			className={className}
			trackingId={trackingId}
			testId={testId}
			{...rest}
		/>
	);
}
PDFViewer.displayName = "PDFViewer";

export { STANDARD_EMBED_CLASS_NAME };
